import {createAsyncThunk} from "@reduxjs/toolkit";
import axios from "axios";
import {boardAPI, ICursor} from "../api";
import {filesAPI} from "../../files/api";
import {COLOR_UPLOAD_PREFIX} from "./image";
import {handlerError} from "../../../store/thunkHandlers";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {AppDispatch, RootState} from "../../../store";
import {IBoard, ICar, ICarColor, ICarEvent, ICarWithEvents, IMoveInput, MilestoneStatus} from "./types";

/**
 * Загрузить доску, если её ещё нет и она не грузится (S4.1): на входе её
 * грузит `AppRealtime`, дальше свежесть держит сокет доски на уровне
 * приложения — заход на доску не перечитывает её второй раз.
 */
export const ensureBoard = () => (dispatch: AppDispatch, getState: () => RootState) => {
    const {loaded, isLoading} = getState().board;
    if (!loaded && !isLoading) dispatch(fetchBoard());
};

/**
 * Ошибка API → всплывающее сообщение с текстом из `message` ответа.
 * 409 (карточка уже изменилась, машину уже взяли) — ещё и перезагрузка доски.
 */
export interface IBoardError {
    message: string;
    status?: number;
}

/** Отказ с 409: доска уже перечитывается, окно можно закрыть. */
export const isConflict = (payload: unknown): boolean => (payload as IBoardError | undefined)?.status === 409;

/** `quiet` — ошибку показывает само окно (добавление машины, S2.2): без всплывающего сообщения и перезагрузки. */
const fail = (e: unknown, dispatch: AppDispatch, quiet = false): IBoardError => {
    const message = handlerError(e);
    const status = axios.isAxiosError(e) ? e.response?.status : undefined;
    if (quiet) return {message, status};
    dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text: message}));
    if (status === 409) dispatch(fetchBoard());
    return {message, status};
};

export const fetchBoard = createAsyncThunk<IBoard, void, {dispatch: AppDispatch}>(
    "board/fetch",
    async (_, {dispatch, rejectWithValue}) => {
        try {
            return await boardAPI.getBoard();
        } catch (e) {
            const text = handlerError(e);
            dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text}));
            return rejectWithValue(text);
        }
    },
);

/**
 * После переподключения сокета: доска, открытый журнал и история открытой
 * машины — события, пропущенные без связи, не догоняются.
 */
export const fetchAfterReconnect = createAsyncThunk<void, void, {dispatch: AppDispatch; state: RootState}>(
    "board/afterReconnect",
    async (_, {dispatch, getState}) => {
        const {journal, selectedId} = getState().board;
        await Promise.all([
            dispatch(fetchBoard()),
            journal.loaded ? dispatch(fetchEvents(undefined)) : null,
            selectedId ? dispatch(fetchCarHistory(selectedId)) : null,
        ]);
    },
);

export const fetchMoreDone = createAsyncThunk<ICar[], ICursor, {dispatch: AppDispatch}>(
    "board/moreDone",
    async (cursor, {dispatch, rejectWithValue}) => {
        try {
            return await boardAPI.getDone(cursor);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

/** Режим «Все / Мои» (S18) — из стора: «Показать ещё» и переподключение грузят в том же режиме. */
export const fetchEvents = createAsyncThunk<
    {items: ICarEvent[]; append: boolean; mine: boolean},
    ICursor | undefined,
    {dispatch: AppDispatch; state: RootState}
>("board/events", async (cursor, {dispatch, getState, rejectWithValue}) => {
    try {
        const {mine} = getState().board.journal;
        return {items: await boardAPI.getEvents(cursor, mine), append: !!cursor, mine};
    } catch (e) {
        return rejectWithValue(fail(e, dispatch));
    }
});

export const fetchCarHistory = createAsyncThunk<ICarWithEvents, string, {dispatch: AppDispatch}>(
    "board/carHistory",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            return await boardAPI.getCar(id);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

/** Мутация машины: ответ — карточка целиком, её кладёт слайс (итог всё равно — по событию). */
const carThunk = <A>(type: string, run: (arg: A) => Promise<ICar>, quiet = false) =>
    createAsyncThunk<ICar, A, {dispatch: AppDispatch}>(type, async (arg, {dispatch, rejectWithValue}) => {
        try {
            return await run(arg);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch, quiet));
        }
    });

/** Отказ (дубль VIN — 409, нет цвета — 400) окно добавления показывает текстом под полем. */
export const fetchCreateCar = carThunk(
    "board/createCar",
    (a: {vin: string; color_id: string}) => boardAPI.createCar(a.vin, a.color_id),
    true,
);
export const fetchUpdateCar = carThunk(
    "board/updateCar",
    (a: {id: string; vin?: string; color_id?: string | null; stage_note?: string | null}) => {
        const {id, ...dto} = a;
        return boardAPI.updateCar(id, dto);
    },
);
export const fetchMoveCar = carThunk("board/move", (a: {id: string} & IMoveInput) => {
    const {id, ...dto} = a;
    return boardAPI.move(id, dto);
});
export const fetchTakeCar = carThunk("board/take", (id: string) => boardAPI.take(id));
export const fetchReleaseCar = carThunk("board/release", (id: string) => boardAPI.release(id));
/** «Стенды» (S18): в ремзоне / на стендах. */
export const fetchSetAtStands = carThunk("board/setAtStands", (a: {id: string; at_stands: boolean}) =>
    boardAPI.setAtStands(a.id, a.at_stands),
);
export const fetchSetMilestone = carThunk(
    "board/setMilestone",
    (a: {id: string; kind: string; status: MilestoneStatus; remark?: string | null}) =>
        boardAPI.setMilestone(a.id, a.kind, a.status, a.remark),
);
export const fetchClearMilestone = carThunk("board/clearMilestone", (a: {id: string; kind: string}) =>
    boardAPI.clearMilestone(a.id, a.kind),
);
export const fetchRaiseAlert = carThunk("board/raiseAlert", (a: {id: string; reason: string}) =>
    boardAPI.raiseAlert(a.id, a.reason),
);
export const fetchTakeAlert = carThunk("board/takeAlert", (id: string) => boardAPI.takeAlert(id));
/** «Я мухожук» (S17.3): отказ от взятого алерта. */
export const fetchReleaseAlert = carThunk("board/releaseAlert", (id: string) => boardAPI.releaseAlert(id));
export const fetchResolveAlert = carThunk("board/resolveAlert", (id: string) => boardAPI.resolveAlert(id));
/** «Вернуть на доску» из Статы (S17.2): карточка снова в «На отгрузку». */
export const fetchUnshipCar = carThunk("board/unship", (id: string) => boardAPI.unship(id));

export const carMutations = [
    fetchCreateCar,
    fetchUpdateCar,
    fetchMoveCar,
    fetchTakeCar,
    fetchReleaseCar,
    fetchSetAtStands,
    fetchSetMilestone,
    fetchClearMilestone,
    fetchRaiseAlert,
    fetchTakeAlert,
    fetchReleaseAlert,
    fetchResolveAlert,
    fetchUnshipCar,
] as const;

/** «Уехала» (S17.2): машина уходит с доски; ошибка (алерт, не «На отгрузку») — текстом бэка. */
export const fetchShipCar = createAsyncThunk<{id: string}, string, {dispatch: AppDispatch}>(
    "board/shipCar",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            return await boardAPI.ship(id);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

export const fetchDeleteCar = createAsyncThunk<{id: string}, string, {dispatch: AppDispatch}>(
    "board/deleteCar",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            return await boardAPI.deleteCar(id);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

/** Новый цвет: картинка — POST /api/file, в цвет пишется `/api/file/static/<имя>`. */
export const fetchCreateColor = createAsyncThunk<ICarColor, {name: string; file: File | null}, {dispatch: AppDispatch}>(
    "board/createColor",
    async ({name, file}, {dispatch, rejectWithValue}) => {
        try {
            const fileName = file ? await filesAPI.upload(file) : null;
            return await boardAPI.createColor(name, fileName ? `${COLOR_UPLOAD_PREFIX}${fileName}` : null);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);
